import React, { useMemo, useRef, useState } from 'react';
import { Controller } from 'react-hook-form';
import {
  checkIsAllDomainsSelected,
  getSelectedDomains,
  reconcileParentSelections,
} from '@components/EntityDrawers/components/inputs/DomainDropdown/helpers';
import { CommonKeys } from '@components/EntityDrawers/constants/keys';
import { ISelectOption } from '@constants/entities/ui';
import useProjectId from '@hooks/useProjectId';
import { Stack, Switch, Typography } from '@mui/material';
import { TreeView } from '@mui/x-tree-view';
import { skipToken } from '@reduxjs/toolkit/query';
import { useGetProjectDomainsQuery } from '@store/services/projects';
import { renderTree } from '@views/Project/components/ScopeFilterModal/ScopeFilterModal';
import { SelectHandler } from '@views/Project/components/ScopeFilterModal/TreeItem';
import { buildTree } from '@views/Project/helpers';

const DomainsPaperBody = () => {
  const projectId = useProjectId();
  const { data: projectDomains } = useGetProjectDomainsQuery(
    projectId || skipToken,
  );
  const [expanded, setExpanded] = useState<string[]>([]);

  /** Cache of individual domain selections before enterprise was toggled on */
  const cachedDomainsRef = useRef<ISelectOption[]>([]);

  const [wideDomain] = useMemo(() => {
    return buildTree(projectDomains ?? []);
  }, [projectDomains]);

  /**
   * Build the full list of all individual domain options from the tree
   * (used when expanding from enterprise back to individual selections).
   */
  const buildAllDomainOptions = (): ISelectOption[] =>
    wideDomain.children?.flatMap((parent) => [
      { value: parent.id, label: parent.name },
      ...(parent.children?.map((child) => ({
        value: child.id,
        label: child.name,
      })) ?? []),
    ]) ?? [];

  const hanleClick = (nodeId: string) => {
    setExpanded((ids) =>
      ids.includes(nodeId)
        ? ids.filter((id) => id !== nodeId)
        : [...ids, nodeId],
    );
  };

  const handleSelect =
    (onChange: (value: any) => void, currentValue: any): SelectHandler =>
    ({ nodeId, checked }) => {
      const isCurrentlyWideSelected = currentValue?.some(
        (d: ISelectOption) => d.value === wideDomain.id,
      );

      // Unchecking a domain while enterprise is active:
      // expand enterprise back to individual domains, then remove the unchecked one
      if (!checked && isCurrentlyWideSelected) {
        let expandedDomains =
          cachedDomainsRef.current.length > 0
            ? [...cachedDomainsRef.current]
            : buildAllDomainOptions();

        // Remove the unchecked domain and enterprise
        expandedDomains = expandedDomains.filter(
          (d) => d.value !== nodeId && d.value !== wideDomain.id,
        );

        onChange(expandedDomains);
        cachedDomainsRef.current = [];
        return;
      }

      if (checked) {
        const domainName =
          projectDomains?.find(({ id }) => id === nodeId)?.name ?? nodeId;

        let newDomainsArray: ISelectOption[] = [
          ...(currentValue ?? []),
          { value: nodeId, label: domainName },
        ];

        // Reconcile parents (auto-add parent if all children now selected)
        newDomainsArray = reconcileParentSelections(
          newDomainsArray,
          projectDomains ?? [],
        );

        // If all domains are now selected, cache selections and switch to enterprise only
        const { isAllDomainsSelected, isWideSelected } =
          checkIsAllDomainsSelected({
            projectDomains: projectDomains ?? [],
            selectedDomains: newDomainsArray,
          });

        if (isAllDomainsSelected && !isWideSelected) {
          cachedDomainsRef.current = [...newDomainsArray];
          onChange([{ value: wideDomain.id, label: wideDomain.name }]);
          return;
        }

        onChange(newDomainsArray);
      } else {
        // Remove the domain and enterprise
        onChange(
          currentValue?.filter(
            (domain: ISelectOption) =>
              domain.value !== nodeId && domain.value !== wideDomain.id,
          ) ?? [],
        );
      }
    };

  return (
    <Stack>
      <Controller
        render={({ field: { value, onChange } }) => {
          const isWideSelected = value?.some(
            (domain: ISelectOption) => domain.value === wideDomain.id,
          );

          const selectedDomains = getSelectedDomains({
            selectedDomains: value,
            wideDomain,
          });

          return (
            <>
              <Stack
                direction="row"
                px={3}
                py={2}
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography color="grey.900">{wideDomain.name}</Typography>

                <Switch
                  checked={isWideSelected}
                  onChange={(_, checked) => {
                    if (checked) {
                      // Cache current selections, set form to enterprise only
                      cachedDomainsRef.current = (value ?? []).filter(
                        (d: ISelectOption) => d.value !== wideDomain.id,
                      );
                      onChange([
                        {
                          value: wideDomain.id,
                          label: wideDomain.name,
                        },
                      ]);
                    } else {
                      // Restore cached individual selections
                      onChange(cachedDomainsRef.current);
                      cachedDomainsRef.current = [];
                    }
                  }}
                />
              </Stack>

              <TreeView
                expanded={expanded}
                disableSelection
                multiSelect
                selected={selectedDomains}
              >
                {wideDomain.children?.map((item) =>
                  renderTree(
                    item,
                    [],
                    handleSelect(onChange, value),
                    hanleClick,
                    undefined,
                    {
                      p: '8px 12px',
                    },
                  ),
                )}
              </TreeView>
            </>
          );
        }}
        name={CommonKeys.Domains}
      />
    </Stack>
  );
};

export default DomainsPaperBody;
